import { useState } from "react";
import UploadDocumentModal, { NewDocument } from "./UploadDocumentModal";

type Doc = {
  name: string;
  type: string;
  status: string;
  statusTone: string;
  expires: string;
  size: string;
  icon: string;
  iconTone: string;
};

const initialDocs: Doc[] = [
  {
    name: "Driver License",
    type: "Identity",
    status: "Verified",
    statusTone: "bg-emerald-50 text-emerald-700",
    expires: "Expires Mar 2029",
    size: "1.2 MB",
    icon: "ri-id-card-line",
    iconTone: "bg-indigo-50 text-indigo-700",
  },
  {
    name: "W-9 Form",
    type: "Payroll",
    status: "Verified",
    statusTone: "bg-emerald-50 text-emerald-700",
    expires: "Filed Feb 2024",
    size: "284 KB",
    icon: "ri-file-text-line",
    iconTone: "bg-brand-50 text-brand-700",
  },
  {
    name: "Background Check",
    type: "Compliance",
    status: "Verified",
    statusTone: "bg-emerald-50 text-emerald-700",
    expires: "Renewed Jan 2026",
    size: "612 KB",
    icon: "ri-shield-check-line",
    iconTone: "bg-emerald-50 text-emerald-700",
  },
  {
    name: "Safety Training Certificate",
    type: "Certification",
    status: "Expires Sep 2026",
    statusTone: "bg-amber-50 text-amber-700",
    expires: "Renewal due in 90 days",
    size: "1.8 MB",
    icon: "ri-award-line",
    iconTone: "bg-amber-50 text-amber-700",
  },
  {
    name: "Direct Deposit Authorization",
    type: "Payroll",
    status: "Verified",
    statusTone: "bg-emerald-50 text-emerald-700",
    expires: "Filed Feb 2024",
    size: "96 KB",
    icon: "ri-bank-card-line",
    iconTone: "bg-brand-50 text-brand-700",
  },
  {
    name: "Employee Handbook (signed)",
    type: "Onboarding",
    status: "Verified",
    statusTone: "bg-emerald-50 text-emerald-700",
    expires: "Signed Feb 2022",
    size: "3.4 MB",
    icon: "ri-book-2-line",
    iconTone: "bg-indigo-50 text-indigo-700",
  },
];

function iconForType(type: string): { icon: string; iconTone: string } {
  switch (type) {
    case "Identity":
      return { icon: "ri-id-card-line", iconTone: "bg-indigo-50 text-indigo-700" };
    case "Payroll":
      return { icon: "ri-bank-card-line", iconTone: "bg-brand-50 text-brand-700" };
    case "Compliance":
      return { icon: "ri-shield-check-line", iconTone: "bg-emerald-50 text-emerald-700" };
    case "Certification":
      return { icon: "ri-award-line", iconTone: "bg-amber-50 text-amber-700" };
    case "Onboarding":
      return { icon: "ri-book-2-line", iconTone: "bg-indigo-50 text-indigo-700" };
    default:
      return { icon: "ri-file-line", iconTone: "bg-cream-100 text-ink-700" };
  }
}

export default function DocumentsPanel() {
  const [docs, setDocs] = useState<Doc[]>(initialDocs);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const handleUpload = (n: NewDocument) => {
    const { icon, iconTone } = iconForType(n.type);
    const newDoc: Doc = {
      name: n.name,
      type: n.type,
      status: "Pending review",
      statusTone: "bg-amber-50 text-amber-700",
      expires: n.expires,
      size: n.size,
      icon,
      iconTone,
    };
    setDocs((prev) => [newDoc, ...prev]);
    setToast(`${n.name} uploaded`);
    window.setTimeout(() => setToast(null), 2800);
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border border-ink-100 rounded-lg p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <i className="ri-folder-3-line text-ink-500"></i>
            <h3 className="font-display text-base font-bold text-ink-900">Employee files</h3>
            <span className="text-[10px] font-bold text-ink-500 bg-cream-100 px-2 py-0.5 rounded-full">
              {docs.length} documents
            </span>
          </div>
          <button
            onClick={() => setUploadOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap"
          >
            <i className="ri-upload-2-line"></i>
            Upload file
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {docs.map((d) => (
            <div
              key={d.name}
              className="group border border-ink-100 rounded-lg p-3.5 hover:border-ink-300 hover:bg-cream-50 transition-colors cursor-pointer"
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-md flex items-center justify-center shrink-0 ${d.iconTone}`}
                >
                  <i className={`${d.icon} text-lg`}></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-ink-900 truncate">
                        {d.name}
                      </div>
                      <div className="text-[10px] text-ink-500 mt-0.5">
                        {d.type} · {d.size}
                      </div>
                    </div>
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${d.statusTone}`}
                    >
                      {d.status}
                    </span>
                  </div>
                  <div className="text-[10px] text-ink-500 mt-2">{d.expires}</div>
                  <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button className="text-[10px] font-bold text-brand-700 hover:text-brand-800 cursor-pointer inline-flex items-center gap-1">
                      <i className="ri-eye-line"></i>
                      View
                    </button>
                    <span className="text-ink-300 text-[10px]">·</span>
                    <button className="text-[10px] font-bold text-brand-700 hover:text-brand-800 cursor-pointer inline-flex items-center gap-1">
                      <i className="ri-download-line"></i>
                      Download
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Compliance summary */}
      <div className="bg-white border border-ink-100 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-shield-keyhole-line text-ink-500"></i>
          <h3 className="font-display text-base font-bold text-ink-900">Compliance status</h3>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="text-center p-3 rounded-md bg-emerald-50 border border-emerald-100">
            <div className="font-display text-2xl font-bold text-emerald-700">5</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mt-0.5">
              Verified
            </div>
          </div>
          <div className="text-center p-3 rounded-md bg-amber-50 border border-amber-100">
            <div className="font-display text-2xl font-bold text-amber-700">1</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mt-0.5">
              Renewing
            </div>
          </div>
          <div className="text-center p-3 rounded-md bg-rose-50 border border-rose-100">
            <div className="font-display text-2xl font-bold text-rose-700">0</div>
            <div className="text-[10px] font-bold uppercase tracking-wider text-rose-700 mt-0.5">
              Expired
            </div>
          </div>
        </div>
      </div>

      <UploadDocumentModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUpload={handleUpload}
      />

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 bg-ink-900 text-white text-xs font-bold px-4 py-2.5 rounded-md flex items-center gap-2 border border-ink-800">
          <i className="ri-check-line text-emerald-400"></i>
          {toast}
        </div>
      )}
    </div>
  );
}